import { invokeSearchCandidates } from "@/lib/invokeSearchCandidates";
import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useCompanyContext } from "@/hooks/useCompanyContext";
import { useActionQueueData } from "@/hooks/useActionQueue";

const TALENT_SEEN_KEY = "cw_talentradar_seen_total";

interface Badges {
  hub: number;
  messages: number;
  talent: number;
  todo: number;
  ai: number;
}

/**
 * Telt "er is iets nieuws" per sidebar-item voor bedrijven.
 * Alle tellingen komen uit dezelfde gecombineerde bevraging als de to-do lijst,
 * zodat de zijbalk geen eigen reeks queries meer afvuurt.
 */
export const useCompanySidebarBadges = (): Badges => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: ctx } = useCompanyContext();

  const { data: queue } = useActionQueueData({
    companyId: ctx?.companyId ?? null,
    userId: ctx?.userId,
  });
  const items = queue?.items || [];

  // Recruitment hub: nieuwe sollicitaties + geaccepteerde uitnodigingen die
  // nog niet zijn doorgezet in de pipeline.
  const hub = items.filter((i) => i.kind === "application" || i.kind === "accepted").length;
  const messages = queue?.counts?.unread_messages ?? 0;
  const todo = items.length;
  const ai = items.filter((i) => i.kind === "ai_suggestion").length;

  const { data: talentTotal = 0 } = useQuery({
    queryKey: ["sidebar-badge-talent-total", ctx?.userId],
    queryFn: async () => {
      const { data, error } = await invokeSearchCandidates({ limit: 1, offset: 0 });
      if (error) return 0;
      return (data as any)?.totalCount ?? 0;
    },
    enabled: !!ctx?.userId,
    staleTime: 10 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
    retry: false,
  });

  // Baseline zetten/bijwerken zodra je TalentRadar bezoekt
  useEffect(() => {
    if (!talentTotal) return;
    if (location.pathname.startsWith("/kandidaten")) {
      localStorage.setItem(TALENT_SEEN_KEY, String(talentTotal));
    } else if (localStorage.getItem(TALENT_SEEN_KEY) === null) {
      localStorage.setItem(TALENT_SEEN_KEY, String(talentTotal));
    }
  }, [talentTotal, location.pathname]);

  const seen = Number(localStorage.getItem(TALENT_SEEN_KEY) || talentTotal || 0);
  const talent = location.pathname.startsWith("/kandidaten")
    ? 0
    : Math.max(0, talentTotal - seen);

  // Live bijwerken: realtime events worden samengevoegd tot maximaal één
  // verversing per twee seconden, zodat drukte geen refetch-storm veroorzaakt.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!ctx?.userId) return;

    const refreshBadges = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["action-queue"] });
      }, 2000);
    };

    const channel = supabase
      .channel(`sidebar-badges-${ctx.userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "matches" }, refreshBadges)
      .on("postgres_changes", { event: "*", schema: "public", table: "messages" }, refreshBadges)
      .on("postgres_changes", { event: "*", schema: "public", table: "match_views" }, refreshBadges)
      .on("postgres_changes", { event: "*", schema: "public", table: "anonymous_applications" }, refreshBadges)
      .on("postgres_changes", { event: "*", schema: "public", table: "ai_recruiter_suggestions" }, refreshBadges)
      .subscribe();

    const onFocus = () => {
      if (document.visibilityState === "visible") refreshBadges();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      supabase.removeChannel(channel);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [ctx?.userId, queryClient]);

  return { hub, messages, talent, todo, ai };
};
